import { useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  admissionAuth,
  deanAuth,
  financeAuth,
  programHeadAuth,
  professorAuth,
  registrarAuth,
  roomManagerAuth,
  studentAuth,
  systemAdministratorAuth,
  vicePresidentAuth,
  defaultAuth
} from "../firebase";

export const ROLE_SCOPES = {
  REGISTRAR: "Registrar",
  ADMISSION: "Admission Counselor",
  FINANCE: "Finance",
  ROOM_MANAGER: "Room Manager",
  SYSTEM_ADMINISTRATOR: "System Administrator",
  DEAN: "Dean",
  PROGRAM_HEAD: "Program Head",
  PROFESSOR: "Professor",
  VICE_PRESIDENT: "Vice President",
  NEW_STUDENT: "New Student",
  ENROLLED_STUDENT: "Enrolled Student"
};

const roleAuth = {
  [ROLE_SCOPES.REGISTRAR]: registrarAuth,
  [ROLE_SCOPES.ADMISSION]: admissionAuth,
  [ROLE_SCOPES.FINANCE]: financeAuth,
  [ROLE_SCOPES.ROOM_MANAGER]: roomManagerAuth,
  [ROLE_SCOPES.SYSTEM_ADMINISTRATOR]: systemAdministratorAuth,
  [ROLE_SCOPES.DEAN]: deanAuth,
  [ROLE_SCOPES.PROGRAM_HEAD]: programHeadAuth,
  [ROLE_SCOPES.PROFESSOR]: professorAuth,
  [ROLE_SCOPES.VICE_PRESIDENT]: vicePresidentAuth,
  [ROLE_SCOPES.NEW_STUDENT]: studentAuth
};

const enrolledSessionKey = "enrollease:auth:enrolled-student";
const enrolledStudentDataKey = "enrolledStudentData";

function channelName(role) {
  return `enrollease-auth:${role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function eventStorageKey(role) {
  return `${channelName(role)}:event`;
}

function createGeneration() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getRoleAuth(role) {
  return roleAuth[role] || null;
}

export function getRoleScopeForRole(role) {
  if (role === "Treasurer" || role === "Financial Officer") return ROLE_SCOPES.FINANCE;
  return role;
}

export function getRoleDashboardPath(role) {
  if (role === "Registrar") return "/registar/dashboard";
  if (role === "Admission Counselor") return "/admission/dashboard";
  if (role === "Treasurer" || role === "Financial Officer" || role === ROLE_SCOPES.FINANCE) return "/finances/dashboard";
  if (role === "Room Manager") return "/room-management";
  if (role === "System Administrator") return "/AdminDashboard";
  if (role === "Dean") return "/dean";
  if (role === "Program Head") return "/program-head";
  if (role === "Professor") return "/professor";
  if (role === "Vice President") return "/vp";
  if (role === "New Student") return "/student-portal/dashboard";
  return "/";
}

export function useRoleSessionState(role) {
  const [state, setState] = useState({ loading: true, user: null });

  useEffect(() => {
    let active = true;
    const auth = getRoleAuth(getRoleScopeForRole(role));
    if (!auth) {
      setState({ loading: false, user: null });
      return () => { active = false; };
    }

    let unsubscribe = () => { };
    const initialize = async () => {
      if (typeof auth.authStateReady === "function") await auth.authStateReady();
      if (!active) return;
      setState({ loading: false, user: auth.currentUser || null });
      unsubscribe = onAuthStateChanged(auth, (user) => {
        if (active) setState({ loading: false, user: user || null });
      });
    };
    initialize();
    return () => {
      active = false;
      unsubscribe();
    };
  }, [role]);

  return state;
}

export function usePortalSessionState(roles) {
  const roleKey = roles.join("|");
  const [state, setState] = useState({ loading: true, role: null, user: null });

  useEffect(() => {
    let active = true;
    const authEntries = roles
      .map((role) => ({ role, auth: getRoleAuth(getRoleScopeForRole(role)) }))
      .filter((entry) => entry.auth);
    let unsubscribers = [];

    const refresh = () => {
      const activeEntry = authEntries.find((entry) => entry.auth.currentUser);
      if (active) setState({ loading: false, role: activeEntry?.role || null, user: activeEntry?.auth.currentUser || null });
    };

    const initialize = async () => {
      await Promise.all(authEntries.map(({ auth }) => (
        typeof auth.authStateReady === "function" ? auth.authStateReady() : Promise.resolve()
      )));
      if (!active) return;
      refresh();
      unsubscribers = authEntries.map(({ auth }) => onAuthStateChanged(auth, refresh));
    };
    initialize();
    return () => {
      active = false;
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [roleKey]);

  return state;
}

export async function waitForRoleAuth(role) {
  const auth = getRoleAuth(getRoleScopeForRole(role));
  if (!auth) return null;
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
    return auth;
  }
  await new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });
  return auth;
}

export async function getCurrentRoleUser(role) {
  const auth = await waitForRoleAuth(role);
  return auth?.currentUser || null;
}

export async function assertRoleLoginAllowed(role, candidateUid) {
  const auth = await waitForRoleAuth(role);
  const currentUser = auth?.currentUser || null;
  if (currentUser && candidateUid && currentUser.uid !== candidateUid) {
    const error = new Error(`Another ${getRoleScopeForRole(role)} account is already active. Log out that account before signing in.`);
    error.code = "auth/role-already-active";
    throw error;
  }
  return auth;
}

export async function assertPortalLoginAllowed(roles, candidateUid) {
  for (const role of roles) {
    await assertRoleLoginAllowed(role, candidateUid);
  }
}

export function subscribeRoleEvents(role, handler) {
  const name = getRoleScopeForRole(role);
  const storageKey = eventStorageKey(name);
  const broadcast = typeof window !== "undefined" && typeof window.BroadcastChannel === "function"
    ? new window.BroadcastChannel(channelName(name))
    : null;

  const handleEvent = (event) => {
    const payload = event?.data || event;
    if (payload?.type) handler(payload);
  };

  const handleStorage = (event) => {
    if (event.key !== storageKey || !event.newValue) return;
    try {
      handleEvent(JSON.parse(event.newValue));
    } catch (error) {
      console.warn("Ignoring malformed role session event", error);
    }
  };

  broadcast?.addEventListener("message", handleEvent);
  window?.addEventListener?.("storage", handleStorage);

  return () => {
    broadcast?.removeEventListener("message", handleEvent);
    broadcast?.close();
    window?.removeEventListener?.("storage", handleStorage);
  };
}

export function publishRoleEvent(role, event) {
  const name = getRoleScopeForRole(role);
  const payload = { ...event, generation: event.generation || createGeneration() };
  if (typeof window !== "undefined" && typeof window.BroadcastChannel === "function") {
    const broadcast = new window.BroadcastChannel(channelName(name));
    broadcast.postMessage(payload);
    broadcast.close();
  }
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.setItem(eventStorageKey(name), JSON.stringify(payload));
  }
}

export async function logoutRole(role) {
  const scope = getRoleScopeForRole(role);
  const auth = await waitForRoleAuth(scope);
  const uid = auth?.currentUser?.uid;
  if (auth) await signOut(auth);
  if (uid && defaultAuth.currentUser?.uid === uid) await signOut(defaultAuth);
  publishRoleEvent(scope, { type: "logout" });
}

export function readEnrolledSession() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(enrolledSessionKey);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
}

export function readEnrolledStudentData() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(enrolledStudentDataKey);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
}

export function writeEnrolledStudentData(student) {
  const safeStudent = {
    id: student?.id || student?.uid || null,
    uid: student?.uid || null,
    studentId: student?.studentId || null,
    idNumber: student?.idNumber || null,
    firstName: student?.firstName || "",
    middleName: student?.middleName || "",
    lastName: student?.lastName || "",
    email: student?.email || "",
    profilePicUrl: student?.profilePicUrl || ""
  };
  window.localStorage.setItem(enrolledStudentDataKey, JSON.stringify(safeStudent));
}

export function writeNewStudentSession(student) {
  const safeStudent = { ...student };
  delete safeStudent.password;
  delete safeStudent.studentpassword;
  delete safeStudent.idToken;
  delete safeStudent.refreshToken;
  window.sessionStorage.setItem("currentStudentSession", JSON.stringify(safeStudent));
}

export function readNewStudentSession() {
  try {
    const value = window.sessionStorage.getItem("currentStudentSession");
    if (!value) return null;
    const student = JSON.parse(value);
    writeNewStudentSession(student);
    return JSON.parse(window.sessionStorage.getItem("currentStudentSession"));
  } catch (error) {
    window.sessionStorage.removeItem("currentStudentSession");
    return null;
  }
}

export function writeEnrolledSession(studentId, authUid) {
  const session = { studentId, ...(authUid ? {authUid} : {}), generation: createGeneration() };
  window.localStorage.setItem(enrolledSessionKey, JSON.stringify(session));
  window.localStorage.setItem("enrolledPortalLogin", "true");
  window.localStorage.setItem("currentUserId", studentId);
  publishRoleEvent(ROLE_SCOPES.ENROLLED_STUDENT, { type: "login", generation: session.generation });
  return session;
}

export function clearEnrolledSession() {
  const session = readEnrolledSession();
  window.localStorage.removeItem(enrolledSessionKey);
  window.localStorage.removeItem(enrolledStudentDataKey);
  window.localStorage.removeItem("enrolledPortalLogin");
  window.localStorage.removeItem("currentUserId");
  if (session?.authUid && defaultAuth.currentUser?.uid === session.authUid) {
    void signOut(defaultAuth);
  }
  publishRoleEvent(ROLE_SCOPES.ENROLLED_STUDENT, { type: "logout" });
}

export { enrolledSessionKey };
